/**
 * Визард «Открытие точки» (владелец 31.10.2026): чистая логика без React — вкладки и адрес, даты, статус карточки,
 * полосы фаз, фильтры и группировка задач, отбор чек-листов и заявок, оптимистичное обновление кеша плана.
 * Покрыта plan-model.test.ts.
 */
import type {
  MyCardTaskSetStatus,
  MyOpeningPlanDto,
  MyPlanCardDto,
  MyPlanEventDto,
  MyPlanStageDto,
  MyPlanSubmissionDto,
  MyPlanTaskDto,
} from '../../lib/api/openingTasks';
import { plural, type TagColor } from '../ds/tokens';
import { patchChecklistItem, patchPartnerDates, patchTaskStatus, todayMsk } from '../opening-tasks/stage-tasks';

// ───────────────────────── Вкладки и адрес ─────────────────────────

export type PlanTab = 'overview' | 'gantt' | 'tasks' | 'checklists' | 'appeals';

/** Значение `?tab=` для вкладок; «Обзор» — без параметра. */
const TAB_PARAM: Record<Exclude<PlanTab, 'overview'>, true> = { gantt: true, tasks: true, checklists: true, appeals: true };

/**
 * Какая вкладка открыта по адресу. Ссылка на помещение (`?room=`) без вкладки ведёт в «Обращения» — там окно помещения.
 * Незнакомое значение — «Обзор».
 */
export function tabFromParams(sp: URLSearchParams): PlanTab {
  const raw = sp.get('tab');
  if (raw && raw in TAB_PARAM) return raw as PlanTab;
  if (!raw && sp.get('room')) return 'appeals';
  return 'overview';
}

/**
 * Кто смотрит план: партнёр — свой, с отметками своих задач; менеджер УК (право «Этапы открытия — просмотр») — план
 * партнёра на странице «Открытие точки», только чтение (владелец 31.10.2026).
 */
export type PlanMode = 'partner' | 'manager';

/** Только чтение: режим «смотреть от лица» или экран менеджера (сервер на запись всё равно ответил бы 403). */
export function isPlanReadOnly(viewAs: boolean, mode: PlanMode): boolean {
  return viewAs || mode === 'manager';
}

/**
 * Параметры адреса менеджера, которые живут РЯДОМ с вкладкой: `card` — открытая карточка, `status` — фильтр списка
 * (по нему «← Все открытия» возвращает на тот же список). При смене вкладки и этапа они остаются; у партнёра
 * их в адресе нет.
 */
const CONTEXT_PARAMS = ['card', 'status'] as const;

/** Из адреса — только `card` и `status`. */
function contextParams(current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams();
  for (const key of CONTEXT_PARAMS) {
    const value = current.get(key);
    if (value) next.set(key, value);
  }
  return next;
}

/**
 * Адрес после смены вкладки: остаются `card` / `status` менеджера, `?tab=` (у «Обзора» — пусто) и `stage` для задач.
 * Окно помещения (`room`) и прочее при смене вкладки закрывается.
 */
export function paramsForTab(current: URLSearchParams, tab: PlanTab): URLSearchParams {
  const next = contextParams(current);
  if (tab !== 'overview') next.set('tab', tab);
  const stage = current.get('stage');
  if (tab === 'tasks' && stage) next.set('stage', stage);
  return next;
}

/** Адрес «задачи этапа»: из списка этапов в Обзоре. `current` — чтобы не потерять `card` / `status` менеджера. */
export function paramsForStage(stageCode: string, current?: URLSearchParams): URLSearchParams {
  const next = current ? contextParams(current) : new URLSearchParams();
  next.set('tab', 'tasks');
  next.set('stage', stageCode);
  return next;
}

/** Адрес «задачи всех этапов»: снимается чип «Этап: …», вкладка «Задачи» остаётся. */
export function paramsWithoutStage(current: URLSearchParams): URLSearchParams {
  const next = paramsForTab(current, 'tasks');
  next.delete('stage');
  return next;
}

// ───────────────────────── Даты и место ─────────────────────────

/** Календарный день `YYYY-MM-DD`: дата без времени — как есть, момент времени — по Москве (как считает сервер). */
export function ymdOf(iso: string | null | undefined): string {
  if (!iso) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : todayMsk(d);
}

/** «12.10» — день и месяц; кривая строка — пусто. */
export function fmtDM(iso: string | null | undefined): string {
  const ymd = ymdOf(iso);
  return ymd ? `${ymd.slice(8, 10)}.${ymd.slice(5, 7)}` : '';
}

/** «12.10.26» — с двузначным годом (ключевые даты шапки). */
export function fmtDMY2(iso: string | null | undefined): string {
  const ymd = ymdOf(iso);
  return ymd ? `${ymd.slice(8, 10)}.${ymd.slice(5, 7)}.${ymd.slice(2, 4)}` : '';
}

/** «12.10.2026» — с полным годом. */
export function fmtDMY(iso: string | null | undefined): string {
  const ymd = ymdOf(iso);
  return ymd ? `${ymd.slice(8, 10)}.${ymd.slice(5, 7)}.${ymd.slice(0, 4)}` : '';
}

/** «12.10 – 16.10»; одна дата — одна; «≈» перед окном — прогноз (даты ещё сдвинутся). */
export function fmtWindow(start: string | null | undefined, end: string | null | undefined, estimated = false): string {
  const a = fmtDM(start);
  const b = fmtDM(end);
  if (!a && !b) return '';
  const body = !a || !b || a === b ? a || b : `${a} – ${b}`;
  return estimated ? `≈ ${body}` : body;
}

/** Город у карточки, пока он не известен (как на бэкенде: opening-stages.ts → NO_CITY). */
const NO_CITY = 'Без города';

/** «Город, адрес»: город один раз (адрес может начинаться с него же), «Без города» не показываем. */
export function placeLabel(city: string, address: string | null): string {
  const c = city.trim() === NO_CITY ? '' : city.trim();
  const a = address?.trim() ?? '';
  if (!a) return c;
  if (!c) return a;
  return a.toLowerCase().startsWith(c.toLowerCase()) ? a : `${c}, ${a}`;
}

/** «5 дней» / «1 день». */
export function daysLabel(n: number): string {
  return `${n} ${plural(n, 'день', 'дня', 'дней')}`;
}

// ───────────────────────── Статус карточки ─────────────────────────

export const CARD_STATUS: Record<MyPlanCardDto['statusV2'], { label: string; color: TagColor }> = {
  active: { label: 'В работе', color: 'blue' },
  paused: { label: 'На паузе', color: 'orange' },
  opened: { label: 'Открыто', color: 'green' },
  archived: { label: 'Архив', color: 'default' },
  cancelled: { label: 'Отменено', color: 'red' },
};

/** «Помещение · этап 3 из 17»; номер неизвестен — только название. */
export function stageTagText(stage: MyPlanCardDto['stage']): string {
  return stage.index > 0 && stage.total > 0 ? `${stage.label} · этап ${stage.index} из ${stage.total}` : stage.label;
}

// ───────────────────────── Полосы фаз ─────────────────────────

/** Цвета фаз пути открытия — через переменные кита (в тёмной теме подстраиваются сами). */
const PHASE_COLOR: Record<string, string> = {
  preparation: 'var(--fk-chart-8)',
  premises: 'var(--fk-info)',
  construction: 'var(--fk-orange)',
  launch: 'var(--fk-success)',
  opening: 'var(--fk-chart-7)',
  support: 'var(--fk-chart-6)',
};

export const phaseColor = (code: string | null | undefined): string => (code && PHASE_COLOR[code]) || 'var(--fk-chart-8)';

/** Бледная версия цвета (будущие этапы фазы). */
export const softColor = (color: string, percent: number): string => `color-mix(in srgb, ${color} ${percent}%, transparent)`;

export interface PhaseView {
  code: string;
  label: string;
  stages: MyPlanStageDto[];
  /** Пройдено этапов фазы. */
  done: number;
  /** Идущий сейчас этап фазы. */
  current: MyPlanStageDto | null;
  /** Окно фазы: самое раннее начало и самый поздний конец её этапов. */
  start: string;
  end: string;
}

/** Фазы в порядке `card.phases`, у каждой — её этапы (по `phaseCode`); фаза без этапов не показывается. */
export function buildPhases(card: Pick<MyPlanCardDto, 'phases' | 'stages'>): PhaseView[] {
  const out: PhaseView[] = [];
  for (const ph of card.phases) {
    const stages = card.stages.filter((s) => s.phaseCode === ph.code);
    if (stages.length === 0) continue;
    out.push({
      code: ph.code,
      label: ph.label,
      stages,
      done: stages.filter((s) => s.state === 'done').length,
      current: stages.find((s) => s.state === 'current') ?? null,
      start: stages.reduce((m, s) => (s.start < m ? s.start : m), stages[0].start),
      end: stages.reduce((m, s) => (s.end > m ? s.end : m), stages[0].end),
    });
  }
  return out;
}

/** «2 из 5 этапов», «0 из 1 этапа». */
export function phaseCounter(p: Pick<PhaseView, 'done' | 'stages'>): string {
  const n = p.stages.length;
  return `${p.done} из ${n} ${plural(n, 'этапа', 'этапов', 'этапов')}`;
}

/** Задач к выполнению сверх срока по этапам (для пометки «просрочено N» в списке этапов). */
export function overdueByStage(tasks: readonly Pick<MyPlanTaskDto, 'stageCode' | 'attention' | 'done'>[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const t of tasks) {
    if (t.done || t.attention !== 'overdue') continue;
    m.set(t.stageCode, (m.get(t.stageCode) ?? 0) + 1);
  }
  return m;
}

/** Текущий этап в списке этапов плана. */
export function currentStage(card: Pick<MyPlanCardDto, 'stage' | 'stages'>): MyPlanStageDto | null {
  return card.stages.find((s) => s.state === 'current') ?? card.stages.find((s) => s.code === card.stage.code) ?? null;
}

// ───────────────────────── Лента ─────────────────────────

/** Свежие события сверху. */
export function sortEvents(events: readonly MyPlanEventDto[]): MyPlanEventDto[] {
  return [...events].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

// ───────────────────────── Задачи: фильтры и группы ─────────────────────────

export type SideFilter = 'mine' | 'uk' | 'all';
export type StatusFilter = 'open' | 'overdue' | 'done' | 'all';

export interface TaskFilters {
  side: SideFilter;
  status: StatusFilter;
  /** Код этапа из адреса; null — все этапы. */
  stage: string | null;
}

export const DEFAULT_TASK_FILTERS: TaskFilters = { side: 'mine', status: 'open', stage: null };

/** «Мои» — всё, что не только у УК (партнёр и «обе стороны»); «УК» — только УК. */
export function matchesSide(task: Pick<MyPlanTaskDto, 'side'>, side: SideFilter): boolean {
  if (side === 'all') return true;
  return side === 'uk' ? task.side === 'uk' : task.side !== 'uk';
}

export function matchesStatus(task: Pick<MyPlanTaskDto, 'done' | 'overdue'>, status: StatusFilter): boolean {
  if (status === 'all') return true;
  if (status === 'done') return task.done;
  if (task.done) return false;
  return status === 'overdue' ? task.overdue : true;
}

export function filterPlanTasks(tasks: readonly MyPlanTaskDto[], f: TaskFilters): MyPlanTaskDto[] {
  return tasks.filter((t) => matchesSide(t, f.side) && matchesStatus(t, f.status) && (!f.stage || t.stageCode === f.stage));
}

/** Сколько задач дали бы фильтры, если поменять одно измерение (для числа на чипе). */
export function countTasks(tasks: readonly MyPlanTaskDto[], f: TaskFilters, change: Partial<TaskFilters>): number {
  return filterPlanTasks(tasks, { ...f, ...change }).length;
}

/** Этап из адреса, который есть в плане; чужой код — null (фильтра нет). */
export function resolveStageFilter(code: string | null, stages: readonly Pick<MyPlanStageDto, 'code'>[]): string | null {
  return code && stages.some((s) => s.code === code) ? code : null;
}

export interface TaskGroup {
  code: string;
  label: string;
  /** Окно этапа; у группы «Другие задачи» — нет. */
  start: string | null;
  end: string | null;
  estimated: boolean;
  state: MyPlanStageDto['state'] | null;
  /** Выполнено / всего задач этапа с выбранной стороны (статус не учитывается: это прогресс этапа). */
  done: number;
  total: number;
  /** Задачи, прошедшие все фильтры. */
  tasks: MyPlanTaskDto[];
}

/**
 * Группы по этапам в порядке `card.stages`. Пустые группы (после фильтров) пропускаются. Задача с неизвестным этапом
 * не теряется — уходит в конец группой «Другие задачи».
 */
export function groupPlanTasks(stages: readonly MyPlanStageDto[], tasks: readonly MyPlanTaskDto[], f: TaskFilters): TaskGroup[] {
  const known = new Set(stages.map((s) => s.code));
  const pool = tasks.filter((t) => matchesSide(t, f.side) && (!f.stage || t.stageCode === f.stage));
  const make = (code: string, label: string, stage: MyPlanStageDto | null, mine: MyPlanTaskDto[]): TaskGroup | null => {
    const shown = mine.filter((t) => matchesStatus(t, f.status));
    if (shown.length === 0) return null;
    return {
      code,
      label,
      start: stage?.start ?? null,
      end: stage?.end ?? null,
      estimated: stage?.estimated ?? false,
      state: stage?.state ?? null,
      done: mine.filter((t) => t.done).length,
      total: mine.length,
      tasks: shown,
    };
  };
  const out: TaskGroup[] = [];
  for (const s of stages) {
    const g = make(s.code, s.label, s, pool.filter((t) => t.stageCode === s.code));
    if (g) out.push(g);
  }
  const orphans = make('other', 'Другие задачи', null, pool.filter((t) => !known.has(t.stageCode)));
  if (orphans) out.push(orphans);
  return out;
}

/** Открытые задачи партнёра (не «только УК») — число на вкладке «Задачи». */
export function openPartnerTaskCount(tasks: readonly Pick<MyPlanTaskDto, 'side' | 'done'>[]): number {
  return tasks.filter((t) => t.side !== 'uk' && !t.done).length;
}

/** Задачи с чек-листом — вкладка «Чеклисты». */
export function checklistTasks(tasks: readonly MyPlanTaskDto[]): MyPlanTaskDto[] {
  return tasks.filter((t) => t.checklist.length > 0);
}

/** Тексты плана, которые зависят от зрителя: «вашей точки» — партнёру, «этой точки» — менеджеру. */
export const PLAN_COPY: Record<PlanMode, { sub: string; noTasks: string; noChecklists: string; appealsSub: string; ukResultsSub: string }> = {
  partner: {
    sub: 'Весь план открытия вашей точки: этапы и сроки, задачи, чек-листы и обращения',
    noTasks: 'В плане вашей точки пока нет задач.',
    noChecklists: 'В плане вашей точки пока нет задач с чек-листами.',
    appealsSub: 'анкета, iiko, эквайринг и другие заявки по вашей точке',
    ukResultsSub: 'то, что УК подготовила для вашей точки',
  },
  manager: {
    sub: 'План открытия партнёра: этапы и сроки, задачи, чек-листы и обращения',
    noTasks: 'В плане этой точки пока нет задач.',
    noChecklists: 'В плане этой точки пока нет задач с чек-листами.',
    appealsSub: 'анкета, iiko, эквайринг, осмотры помещений и другие заявки по этой точке',
    ukResultsSub: 'то, что УК подготовила для этой точки',
  },
};

/** Подпись пустого результата вкладки «Задачи» — с подсказкой, что переключить. */
export function emptyTasksText(f: TaskFilters, allTasksCount: number, matchingAnyStatus: number, mode: PlanMode = 'partner'): string {
  if (allTasksCount === 0) return PLAN_COPY[mode].noTasks;
  if (f.status === 'open' && matchingAnyStatus > 0) return 'Открытых задач нет — всё выполнено. Выберите «Выполненные» или «Все», чтобы увидеть сделанное.';
  if (f.status === 'overdue') return 'Просроченных задач нет.';
  return 'По выбранным фильтрам задач нет.';
}

// ───────────────────────── Заявки (вкладка «Обращения») ─────────────────────────

/** Осмотр помещения (в «Обращениях» он — отдельный блок «Мои помещения» с перепиской). */
export const ROOM_INSPECTION_TYPE = 'ROOM_INSPECTION';

/**
 * Заявки по открытию, свежие сверху. Партнёру — без осмотров помещений (они выше, в «Моих помещениях» с перепиской);
 * менеджеру — все, включая осмотры: блока с перепиской у него нет (она в CRM).
 */
export function appealSubmissions(subs: readonly MyPlanSubmissionDto[], mode: PlanMode = 'partner'): MyPlanSubmissionDto[] {
  const shown = mode === 'manager' ? [...subs] : subs.filter((s) => s.type !== ROOM_INSPECTION_TYPE);
  return shown.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** Статусы заявок — подписи и цвета как в CRM (lib/formSubmissions.ts: FORM_STATUS_LABEL / FORM_STATUS_COLOR). */
const SUBMISSION_STATUS: Record<string, { label: string; color: TagColor }> = {
  DRAFT: { label: 'Черновик', color: 'default' },
  SUBMITTED: { label: 'Отправлена', color: 'blue' },
  UNDER_REVIEW: { label: 'На рассмотрении', color: 'orange' },
  REJECTED: { label: 'Отклонена', color: 'red' },
  APPROVED: { label: 'Одобрена', color: 'green' },
  CONVERTED: { label: 'Утверждено', color: 'green' },
};

export function submissionStatus(status: string): { label: string; color: TagColor } {
  return SUBMISSION_STATUS[status] ?? { label: status, color: 'default' };
}

// ───────────────────────── Оптимистичное обновление кеша плана ─────────────────────────

type Attention = MyPlanTaskDto['attention'];
type GanttStatus = MyPlanTaskDto['ganttStatus'];

function attentionAfter(prev: MyPlanTaskDto, next: Pick<MyPlanTaskDto, 'done' | 'overdue'>): Attention {
  if (next.done) return null;
  if (next.overdue) return 'overdue';
  // «Срок скоро» заново не считаем (правило на сервере) — повторная загрузка поправит.
  return prev.attention === 'overdue' ? null : prev.attention;
}

function ganttAfter(prev: MyPlanTaskDto, status: MyCardTaskSetStatus): GanttStatus {
  if (status === 'done') return 'done';
  if (status === 'in_progress') return 'in_progress';
  // «Вернуть в план» / «снять Сделано»: бар снова «План» (точнее скажет повторная загрузка).
  return prev.ganttStatus === 'done' || prev.ganttStatus === 'in_progress' ? 'plan' : prev.ganttStatus;
}

const bump = (n: number, delta: number, max = Number.POSITIVE_INFINITY): number => Math.max(0, Math.min(max, n + delta));

/** Копия плана с новыми датами партнёра у задачи (оптимистично; FB-32 — только для информации). Чужой id — как есть. */
export function withPlanPartnerDates(data: MyOpeningPlanDto, taskId: string, dates: { partnerStartDate?: string | null; partnerDueDate?: string | null }): MyOpeningPlanDto {
  const card = data.card;
  if (!card || !card.tasks.some((t) => t.id === taskId)) return data;
  return { ...data, card: { ...card, tasks: card.tasks.map((t) => (t.id === taskId ? patchPartnerDates(t, dates) : t)) } };
}

/**
 * Копия ответа плана с новым статусом задачи (оптимистично): сама задача, счётчик «выполнено» её этапа и счётчики
 * просрочки / «срок скоро» шапки. Чужой id — как есть. Точнее скажет повторная загрузка.
 */
export function withPlanTaskStatus(data: MyOpeningPlanDto, taskId: string, status: MyCardTaskSetStatus, now: Date = new Date()): MyOpeningPlanDto {
  const card = data.card;
  const prev = card?.tasks.find((t) => t.id === taskId);
  if (!card || !prev) return data;
  const patched = patchTaskStatus(prev, status, now);
  const next: MyPlanTaskDto = { ...patched, attention: attentionAfter(prev, patched), ganttStatus: ganttAfter(prev, status) };
  const doneDelta = Number(next.done) - Number(prev.done);
  const overdueDelta = Number(next.attention === 'overdue') - Number(prev.attention === 'overdue');
  const soonDelta = Number(next.attention === 'soon') - Number(prev.attention === 'soon');
  return {
    ...data,
    card: {
      ...card,
      tasks: card.tasks.map((t) => (t.id === taskId ? next : t)),
      stages:
        doneDelta === 0
          ? card.stages
          : card.stages.map((s) => (s.code === prev.stageCode ? { ...s, tasksDone: bump(s.tasksDone, doneDelta, s.tasksTotal) } : s)),
      overdueTasks: bump(card.overdueTasks, overdueDelta),
      soonTasks: bump(card.soonTasks, soonDelta),
    },
  };
}

/** Копия ответа плана с новой отметкой пункта чек-листа (оптимистично). Чужой id или индекс — как есть. */
export function withPlanChecklistItem(data: MyOpeningPlanDto, taskId: string, index: number, done: boolean, url?: string | null): MyOpeningPlanDto {
  const card = data.card;
  if (!card) return data;
  return { ...data, card: { ...card, tasks: card.tasks.map((t) => (t.id === taskId ? patchChecklistItem(t, index, done, url) : t)) } };
}
